/**
 * ScheduledReportsPage — manage scheduled reports + view execution history.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import HelpButton from '../Guides/HelpButton';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Calendar, Plus, Trash2, Play, ToggleLeft, ToggleRight, Pencil,
  Clock, Mail, CheckCircle, AlertCircle, History, FileText, Filter
} from 'lucide-react';
import ScheduleReportDialog from './ScheduleReportDialog';

const fmtDate = (d, tz) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      timeZone: tz || undefined,
    });
  } catch { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
};

const FREQ_LABELS = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const DOW_SHORT = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };

export default function ScheduledReportsPage() {
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [activeTab, setActiveTab] = useState('schedules');
  const [historyFilter, setHistoryFilter] = useState('');
  const [systemTz, setSystemTz] = useState('UTC');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/scheduled-reports/');
      setSchedules(r.data?.data || []);
      if (r.data?.system_timezone) setSystemTz(r.data.system_timezone);
    } catch { toast.error('Failed to load schedules'); }
    setLoading(false);
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const params = historyFilter ? `?schedule_id=${historyFilter}` : '';
      const r = await api.get(`/api/scheduled-reports/history${params}`);
      setHistory(r.data?.data || []);
      if (r.data?.system_timezone) setSystemTz(r.data.system_timezone);
    } catch { toast.error('Failed to load history'); }
  }, [historyFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === 'history') loadHistory(); }, [activeTab, loadHistory]);

  const handleToggle = async (id) => {
    try {
      const r = await api.post(`/api/scheduled-reports/${id}/toggle`);
      toast.success(r.data.enabled ? 'Schedule enabled' : 'Schedule paused');
      load();
    } catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduled report and its history?')) return;
    try {
      await api.delete(`/api/scheduled-reports/${id}`);
      toast.success('Schedule deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleRunNow = async (id) => {
    try {
      await api.post(`/api/scheduled-reports/${id}/run-now`);
      toast.success('Report queued — email will be sent shortly');
      setTimeout(() => { load(); if (activeTab === 'history') loadHistory(); }, 5000);
    } catch { toast.error('Failed to queue'); }
  };

  const openCreate = () => { setEditData(null); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditData({
      id: s.id, name: s.name, module: s.module, report_id: s.report_id,
      report_label: s.report_label, module_label: s.module_label,
      filters: s.filters || {}, frequency: s.frequency,
      day_of_week: s.day_of_week, day_of_month: s.day_of_month,
      time_of_day: s.time_of_day, recipients: s.recipients?.length ? s.recipients : [''],
    });
    setDialogOpen(true);
  };

  const tzShort = systemTz?.split('/').pop()?.replace(/_/g, ' ') || 'UTC';

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Calendar className="h-5 w-5 text-primary" /> Scheduled Reports
            <HelpButton guideId="reports" label="Reports & Scheduled Reports Guide" />
          </h1>
          <p className="text-xs text-slate-600 mt-0.5">
            {schedules.length} schedule(s) &middot; Timezone: {systemTz}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="create-schedule-btn">
          <Plus className="h-4 w-4 mr-1" /> New Schedule
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-4" data-testid="scheduled-reports-tabs">
        {[
          { id: 'schedules', label: 'Schedules', icon: Calendar, count: schedules.length },
          { id: 'history', label: 'History', icon: History },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`sr-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-slate-600 hover:text-foreground'
            }`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
            {t.count > 0 && <span className="ml-1 text-[10px] bg-muted text-slate-600 rounded-full px-1.5">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Schedules Tab */}
      {activeTab === 'schedules' && (
        <>
          {loading ? (
            <div className="text-center py-12 text-slate-600 text-sm">Loading...</div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-16 border rounded-xl" data-testid="no-schedules">
              <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-slate-800">No Scheduled Reports</h3>
              <p className="text-xs text-slate-600 mt-1 max-w-sm mx-auto">
                Create a schedule to automatically run reports and email the results as CSV attachments.
              </p>
              <Button className="mt-4" size="sm" onClick={openCreate}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Create First Schedule
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="schedule-list">
              {schedules.map(s => (
                <div key={s.id} className={`border rounded-xl p-4 hover:shadow-sm transition-shadow ${!s.enabled ? 'opacity-60' : ''}`}
                  data-testid={`schedule-${s.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{s.name}</span>
                        <Badge variant="outline" className="text-[10px]">{s.module_label}</Badge>
                        <Badge className={`text-[10px] ${s.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {s.enabled ? 'Active' : 'Paused'}
                        </Badge>
                        {s.run_count > 0 && <Badge variant="secondary" className="text-[10px]">{s.run_count} run{s.run_count !== 1 ? 's' : ''}</Badge>}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{s.report_label}</p>

                      <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {FREQ_LABELS[s.frequency] || s.frequency}
                          {s.frequency === 'weekly' && ` (${DOW_SHORT[s.day_of_week] || ''})`}
                          {s.frequency === 'monthly' && ` (day ${s.day_of_month})`}
                          {' at '}{s.time_of_day || '08:00'} {tzShort}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {(s.recipients || []).length} recipient{(s.recipients || []).length !== 1 ? 's' : ''}
                        </span>
                        {s.next_run && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Next: {fmtDate(s.next_run, systemTz)}
                          </span>
                        )}
                        {s.last_run && (
                          <span className="flex items-center gap-1">
                            {s.last_status === 'success' ? <CheckCircle className="h-3 w-3 text-green-600" /> : <AlertCircle className="h-3 w-3 text-red-500" />}
                            Last: {fmtDate(s.last_run, systemTz)} ({s.last_row_count ?? '—'} rows)
                          </span>
                        )}
                      </div>

                      {Object.entries(s.filters || {}).filter(([, v]) => v).length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {Object.entries(s.filters).filter(([, v]) => v).map(([k, v]) => (
                            <Badge key={k} variant="secondary" className="text-[9px]">{k}: {v}</Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleRunNow(s.id)} title="Run Now"
                        data-testid={`schedule-run-${s.id}`}>
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleToggle(s.id)} title={s.enabled ? 'Pause' : 'Enable'}
                        data-testid={`schedule-toggle-${s.id}`}>
                        {s.enabled ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)} title="Edit"
                        data-testid={`schedule-edit-${s.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(s.id)} title="Delete"
                        data-testid={`schedule-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4" data-testid="report-history">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <Filter className="h-3.5 w-3.5" /> Filter by schedule:
            </div>
            <select value={historyFilter} onChange={e => setHistoryFilter(e.target.value)}
              className="h-8 border rounded-md px-2 text-sm" data-testid="history-filter">
              <option value="">All Schedules</option>
              {schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span className="text-xs text-slate-600">{history.length} execution(s)</span>
          </div>

          {history.length === 0 ? (
            <div className="text-center py-12 border rounded-xl">
              <History className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600">No execution history yet</p>
              <p className="text-xs text-slate-500 mt-1">History will appear after scheduled reports are run.</p>
            </div>
          ) : (
            <div className="border rounded-xl overflow-hidden" data-testid="history-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-700">Schedule</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-700">Report</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-700">Executed ({tzShort})</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-700">Rows</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-700">Duration</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-700">Recipients</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={i} className="border-b hover:bg-accent/20 transition-colors" data-testid={`history-row-${i}`}>
                      <td className="px-4 py-2.5 font-medium">{h.schedule_name}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-[10px]">{h.module_label}</Badge>
                        <span className="ml-1.5 text-xs text-slate-600">{h.report_label}</span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(h.executed_at, systemTz)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{(h.row_count || 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-xs text-slate-600">
                        {h.duration_seconds != null ? `${h.duration_seconds.toFixed(1)}s` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">{(h.recipients || []).join(', ')}</td>
                      <td className="px-4 py-2.5">
                        {h.status === 'success' ? (
                          <Badge className="text-[10px] bg-green-100 text-green-700"><CheckCircle className="h-2.5 w-2.5 inline mr-0.5" />Success</Badge>
                        ) : (
                          <Badge className="text-[10px] bg-red-100 text-red-700"><AlertCircle className="h-2.5 w-2.5 inline mr-0.5" />Failed</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ScheduleReportDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} prefill={editData} />
    </AppShell>
  );
}
